import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { IApiSageClass, IApiSageClassFilter, IApiSageClassFilterType } from '../../modules/graphql/types/types';
import { unwrapConnection } from '../../rxjs.pipes';
import { CompanyService } from '../../services';
import { of, Subject, timer } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-sage-class-select-kendo',
  templateUrl: './sage-class-select-kendo.component.html',
  styleUrls: ['./sage-class-select-kendo.component.scss']
})
export class SageClassSelectKendoComponent implements OnChanges, OnInit, OnDestroy {
  @ViewChild('multiSelect', { static: false }) public multiSelect: MultiSelectComponent;

  @Input() public label = '';
  @Input() public placeholder = 'Search Sage class';
  @Input() public disabled = false;
  @Input() public selectedId: string[] = [];

  @Output() public selectionChanged = new EventEmitter<string[]>();
  @Output() public selectedSageClassesChanged = new EventEmitter<IApiSageClass[]>();
  @Output() public enteredPressed = new EventEmitter<void>();

  public sageClasses: IApiSageClass[] = [];
  public selectedValue: string[] = [];
  public loading = false;
  public searchString = '';
  private selectedSageClasses: IApiSageClass[] = [];
  private searchTerms$ = new Subject<string>();
  private destroyed$ = new Subject<void>();

  constructor(private companyService: CompanyService) { }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedId) {
      this.selectedValue = changes.selectedId.currentValue || [];
      this.selectedSageClasses = this.selectedSageClasses.filter(item => this.selectedValue.includes(item.id));
    }
  }

  public ngOnInit(): void {
    this.searchTerms$.pipe(
      distinctUntilChanged(),
      switchMap(search => timer(search ? 300 : 0).pipe(
        tap(() => this.loading = !!search),
        switchMap(() => {
          if (!search) {
            return of({ search, results: null as IApiSageClass[] | null });
          }

          const filters: IApiSageClassFilter[] = [{
            type: IApiSageClassFilterType.Name,
            value: search
          }];

          return this.companyService.getSageClasses(filters).pipe(
            unwrapConnection(),
            map((results: IApiSageClass[]) => ({ search, results })),
            catchError(() => of({ search, results: [] as IApiSageClass[] }))
          );
        })
      )),
      takeUntil(this.destroyed$)
    ).subscribe(({ search, results }) => {
      this.loading = false;

      if (!search || !results) {
        this.sageClasses = this.selectedSageClasses;
        return;
      }

      const selectedIds = new Set(this.selectedSageClasses.map(item => item.id));
      this.sageClasses = [...this.selectedSageClasses, ...results.filter(item => !selectedIds.has(item.id))];
      this.multiSelect.toggle(true);
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onSearchChange(search: string): void {
    this.searchString = search?.trim() || '';
    this.multiSelect.toggle(false);

    if (!this.searchString) {
      this.loading = false;
      this.sageClasses = this.selectedSageClasses;
    }
    this.searchTerms$.next(this.searchString);
  }

  public onValueChange(ids: string[]): void {
    this.selectedValue = ids || [];
    this.selectedSageClasses = this.sageClasses.filter(item => this.selectedValue.includes(item.id));
    this.selectionChanged.emit(this.selectedValue);
    this.selectedSageClassesChanged.emit(this.selectedSageClasses);
  }

  public onFocus(): void {
    this.sageClasses = this.selectedSageClasses;
  }

  public onEnterPress(): void {
    this.enteredPressed.emit();
  }

  public tagMapper(tags: any[]): any[] {
    return tags.length < 1 ? tags : [tags];
  }
}
